import React from "react";
import {
  act,
  fireEvent,
  render as rtlRender,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { RenderOptions } from "@testing-library/react";
import { EmbeddedCanvasRenderer } from "./EmbeddedCanvasRenderer";

const render = (ui: React.ReactElement, options?: RenderOptions) => {
  const queryClient = new QueryClient();
  return rtlRender(ui, {
    wrapper: ({ children }) => (
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    ),
    ...options,
  });
};

const mockAuthFetch = jest.fn();
const mockUseChildCanvasPreview = jest.fn();
let mockCanvasCapabilities: {
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
};
const mockCapabilitiesFetch = jest.fn();
const mockUpsertFilter = jest.fn();
const mockRemoveFilter = jest.fn();
let mockRuntimeBus = {
  getSelectedRow: () => null,
  upsertRuntimeFilter: mockUpsertFilter,
  removeRuntimeFilter: mockRemoveFilter,
};
jest.mock("../../../../../utils/useAuthFetch", () => ({
  useAuthFetch:
    () =>
    (...args: unknown[]) =>
      args[1] === "fetch canvas data"
        ? mockCapabilitiesFetch(...args)
        : mockAuthFetch(...args),
}));
jest.mock("../../../hooks/useChildCanvasPreview", () => ({
  useChildCanvasPreview: () => mockUseChildCanvasPreview(),
}));
jest.mock("../CanvasRuntimeContext", () => ({
  useCanvasRuntime: () => ({
    canvasId: "host-canvas",
    embeddingPath: [{ canvasId: "outer-canvas", elementId: "outer-host" }],
    interactionBus: mockRuntimeBus,
  }),
  CanvasRuntimeProvider: ({
    children,
    value,
  }: {
    children: React.ReactNode;
    value: { onRefresh?: () => void };
  }) => (
    <>
      {children}
      <button onClick={value.onRefresh}>Refresh file row</button>
    </>
  ),
  buildDataSourceSelectionKey: () => "selection",
  buildRuntimeFilterScopeKey: () => "filter",
}));
jest.mock("../CanvasRenderer", () => ({
  CanvasRenderer: ({
    fieldState,
  }: {
    fieldState: Record<
      string,
      { value?: unknown; onChange?: (value: string) => void }
    >;
  }) => (
    <>
      <span>{String(fieldState.name?.value ?? "")}</span>
      <button onClick={() => fieldState.name?.onChange?.("Changed")}>
        Fill name
      </button>
    </>
  ),
}));

jest.mock("./CanvasDataWidgetRenderer", () => ({
  CanvasDataWidgetRenderer: () => <div>Collection widget</div>,
}));

const childCanvas = {
  id: "child-canvas",
  name: "Child",
  data_sources: [{ alias: "main", view_id: "view-1" }],
  elements: [
    {
      id: "name-field",
      element_type: "FIELD",
      effective_access: "read-write",
      data_binding: { data_source_alias: "main", field_path: "name" },
      config_json: {},
    },
  ],
};
const hostElement = {
  id: "host-element",
  type: "embedded_canvas",
  props: {
    childCanvasId: "child-canvas",
    parentKeyField: "id",
    childDataSourceAlias: "main",
    childFilterField: "parent_id",
  },
  layout: { gridColumn: 1, gridRow: 1, columnSpan: 12, rowSpan: 4 },
};

describe("EmbeddedCanvasRenderer writes", () => {
  it("does not reinstall a filter when only the bus state snapshot changes", async () => {
    mockUpsertFilter.mockClear();
    mockRemoveFilter.mockClear();
    mockAuthFetch.mockResolvedValue({
      rows: [{ __row_id: "row-1", name: "Old" }],
    });
    const form = (
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />
    );
    const { rerender } = render(form);
    await screen.findByText("Old");
    expect(mockUpsertFilter).toHaveBeenCalledTimes(1);
    mockRuntimeBus = { ...mockRuntimeBus };
    rerender(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    expect(mockUpsertFilter).toHaveBeenCalledTimes(1);
    expect(mockRemoveFilter).not.toHaveBeenCalled();
    rerender(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "new-parent-key", __row_id: "parent-2" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    expect(mockUpsertFilter).toHaveBeenCalledTimes(2);
    expect(mockRemoveFilter).toHaveBeenCalledTimes(1);
  });
  it.each(["parent", "workspace", "child", "view", "alias", "fk", "host"])(
    "invalidates old writable rows while the %s scope query is pending",
    async (changed) => {
      const pendingFetches: Array<(value: unknown) => void> = [];
      let refetchesReleased = false;
      let queries = 0;
      mockAuthFetch.mockImplementation(async (_url, tag) => {
        if (tag !== "fetch child records") return { success: true };
        if (++queries === 1)
          return { rows: [{ __row_id: "alice-child", name: "Alice private" }] };
        if (refetchesReleased)
          return { rows: [{ __row_id: "bob-child", name: "Bob current" }] };
        return new Promise((resolve) => {
          pendingFetches.push(resolve);
        });
      });
      const original = {
        element: hostElement as any,
        parentData: { id: "alice", __row_id: "alice" },
        workspaceId: "workspace-1",
        parentCanvasId: "host-canvas",
      };
      const { rerender } = render(<EmbeddedCanvasRenderer {...original} />);
      const oldValue = await screen.findByText("Alice private");
      const oldSave = screen.getByRole("button", { name: "Save" });
      const next = { ...original };
      if (changed === "parent")
        next.parentData = { id: "bob", __row_id: "bob" };
      if (changed === "workspace") next.workspaceId = "workspace-2";
      if (changed === "host") next.parentCanvasId = "other-host";
      if (["child", "alias", "fk"].includes(changed)) {
        next.element = {
          ...hostElement,
          props: {
            ...hostElement.props,
            ...(changed === "child" ? { childCanvasId: "other-child" } : {}),
            ...(changed === "alias" ? { childDataSourceAlias: "other" } : {}),
            ...(changed === "fk" ? { childFilterField: "other_parent" } : {}),
          },
        };
      }
      if (changed === "view")
        mockUseChildCanvasPreview.mockReturnValue({
          canvas: {
            ...childCanvas,
            data_sources: [{ alias: "main", view_id: "other-view" }],
          },
          loading: false,
          error: null,
        });
      rerender(<EmbeddedCanvasRenderer {...next} />);
      await waitFor(() => expect(queries).toBeGreaterThan(1));
      // Try activating the former writable action while the new query is pending.
      fireEvent.click(oldSave);
      expect(oldValue).not.toBeInTheDocument();
      expect(screen.queryByRole("button", { name: "Save" })).toBeNull();
      expect(
        screen.queryByRole("button", { name: "Delete child record" }),
      ).toBeNull();
      expect(
        mockAuthFetch.mock.calls.filter(
          ([, tag]) => tag !== "fetch child records",
        ),
      ).toHaveLength(0);
      await act(async () => {
        refetchesReleased = true;
        pendingFetches
          .splice(0)
          .forEach((finish) =>
            finish({ rows: [{ __row_id: "bob-child", name: "Bob current" }] }),
          );
      });
      expect(await screen.findByText("Bob current")).toBeInTheDocument();
      expect(screen.getByRole("button", { name: "Save" })).toBeInTheDocument();
    },
  );
  it("composes mixed-child row context with pending parent-switch Save removal", async () => {
    mockUseChildCanvasPreview.mockReturnValue({
      canvas: {
        ...childCanvas,
        elements: [
          ...childCanvas.elements,
          {
            id: "board",
            element_type: "WIDGET",
            config_json: {
              widget_type: "KANBAN",
              data_source_alias: "main",
            },
          },
        ],
      },
      loading: false,
      error: null,
    });
    let finish!: (value: unknown) => void;
    let queries = 0;
    mockAuthFetch.mockImplementation(async (_url, tag) => {
      if (tag !== "fetch child records") return { success: true };
      if (++queries === 1)
        return { rows: [{ _rowId: "alice-child", name: "Alice mixed" }] };
      return new Promise((resolve) => {
        finish = resolve;
      });
    });
    const props = {
      element: hostElement as any,
      parentData: { id: "alice", __row_id: "alice" },
      workspaceId: "workspace-1",
      parentCanvasId: "host-canvas",
    };
    const { rerender } = render(<EmbeddedCanvasRenderer {...props} />);
    expect(await screen.findByText("Alice mixed")).toBeVisible();
    const oldSave = screen.getByRole("button", { name: "Save" });
    fireEvent.click(screen.getByRole("button", { name: "Refresh file row" }));
    await waitFor(() => expect(queries).toBe(2));
    expect(screen.getByText("Alice mixed")).toBeVisible();
    rerender(
      <EmbeddedCanvasRenderer
        {...props}
        parentData={{ id: "bob", __row_id: "bob" }}
      />,
    );
    await waitFor(() => expect(queries).toBe(3));
    fireEvent.click(oldSave);
    expect(screen.queryByText("Alice mixed")).not.toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "Save" }),
    ).not.toBeInTheDocument();
    expect(
      mockAuthFetch.mock.calls.filter(
        ([, tag]) => tag !== "fetch child records",
      ),
    ).toHaveLength(0);
    await act(async () =>
      finish({ rows: [{ _rowId: "bob-child", name: "Bob mixed" }] }),
    );
    expect(await screen.findByText("Bob mixed")).toBeVisible();
    expect(screen.getByRole("button", { name: "Save" })).toBeVisible();
  });

  it("keeps the loaded form mounted while refreshing its row", async () => {
    let finish!: (value: unknown) => void;
    let queries = 0;
    mockAuthFetch.mockImplementation(async (_url, tag) => {
      if (tag !== "fetch child records") return { success: true };
      if (++queries === 1)
        return { rows: [{ __row_id: "row-file", name: "Before refresh" }] };
      return new Promise((resolve) => {
        finish = resolve;
      });
    });
    render(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    const formValue = await screen.findByText("Before refresh");
    fireEvent.click(screen.getByRole("button", { name: "Refresh file row" }));
    await waitFor(() => expect(queries).toBe(2));
    expect(formValue).toBeInTheDocument();
    expect(
      screen.getByRole("status", { name: "Refreshing records" }),
    ).toBeInTheDocument();
    await act(async () =>
      finish({ rows: [{ __row_id: "row-file", name: "After refresh" }] }),
    );
    expect(await screen.findByText("After refresh")).toBe(formValue);
    expect(
      screen.queryByRole("status", { name: "Refreshing records" }),
    ).toBeNull();
  });
  it("reloads the embedded row after a file mutation", async () => {
    let currentName = "Before file write";
    mockAuthFetch.mockImplementation(async (_url, tag) =>
      tag === "fetch child records"
        ? { rows: [{ __row_id: "row-file", name: currentName }] }
        : { success: true },
    );
    render(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    await screen.findByText("Before file write");
    currentName = "Server row after file write";
    fireEvent.click(screen.getByRole("button", { name: "Refresh file row" }));
    await screen.findByText("Server row after file write");
  });
  beforeEach(() => {
    mockUseChildCanvasPreview.mockReturnValue({
      canvas: childCanvas,
      loading: false,
      error: null,
    });
    mockAuthFetch.mockReset();
    mockCanvasCapabilities = {
      can_create: true,
      can_edit: true,
      can_delete: true,
    };
    mockCapabilitiesFetch.mockReset();
    mockCapabilitiesFetch.mockImplementation(
      async () => mockCanvasCapabilities,
    );
  });

  it("can integrate a compact form without its repeated title, retaining authorized save identity", async () => {
    mockAuthFetch.mockImplementation(async (_url, tag) =>
      tag === "fetch child records"
        ? { rows: [{ __row_id: "row-compact", name: "Old" }] }
        : { success: true },
    );
    render(
      <EmbeddedCanvasRenderer
        element={
          {
            ...hostElement,
            props: {
              ...hostElement.props,
              presentation: "compact",
              showTitle: false,
            },
          } as any
        }
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    await screen.findByText("Old");
    expect(screen.queryByText("Child")).not.toBeInTheDocument();
    expect(
      // The compact surface is the ancestor card, not an accessible control.
      // eslint-disable-next-line testing-library/no-node-access
      screen.getByRole("button", { name: "Save" }).closest(".MuiCard-root"),
    ).toHaveStyle({ padding: "0px", backgroundColor: "transparent" });
    expect(
      screen.getByRole("button", { name: "Delete child record" }),
    ).toBeInTheDocument();
    fireEvent.click(screen.getByRole("button", { name: "Fill name" }));
    fireEvent.click(screen.getByRole("button", { name: "Save" }));
    await waitFor(() => {
      const call = mockAuthFetch.mock.calls.find(
        ([, tag]) => tag === "update child record",
      );
      expect(call && JSON.parse(call[2].body).record_id).toBe("row-compact");
    });
  });

  it.each([
    [
      "regular edit-only user",
      { can_create: false, can_edit: true, can_delete: false },
      false,
    ],
    ["admin", { can_create: true, can_edit: true, can_delete: true }, true],
  ])(
    "composes child capabilities for %s",
    async (_label, capabilities, seesDelete) => {
      mockCanvasCapabilities = capabilities;
      mockAuthFetch.mockResolvedValue({
        rows: [{ __row_id: "row-1", name: "Old" }],
      });
      render(
        <EmbeddedCanvasRenderer
          element={hostElement as any}
          parentData={{ id: "parent-key", __row_id: "parent-1" }}
          workspaceId="workspace-1"
          parentCanvasId="host-canvas"
        />,
      );
      await screen.findByRole("button", { name: "Save" });
      expect(
        screen.queryByRole("button", { name: "Delete child record" }) !== null,
      ).toBe(seesDelete);
      expect(screen.getByRole("button", { name: "Save" })).toBeInTheDocument();
    },
  );

  it("requests capabilities without integer child identity and retains authorized save/delete", async () => {
    mockCapabilitiesFetch.mockImplementation(async (_url, _tag, options) => {
      const request = JSON.parse(options.body);
      if (request.record_id !== null) {
        throw Object.assign(new Error("record_id must be a UUID"), {
          status: 422,
        });
      }
      return mockCanvasCapabilities;
    });
    mockAuthFetch.mockImplementation(async (_url, tag) =>
      tag === "fetch child records"
        ? { rows: [{ __row_id: 7, name: "Integer child" }] }
        : { success: true },
    );
    render(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    await screen.findByText("Integer child");
    expect(
      mockCapabilitiesFetch.mock.calls.every(
        ([, , options]) => JSON.parse(options.body).record_id === null,
      ),
    ).toBe(true);
    expect(screen.getByRole("button", { name: "Save" })).toBeInTheDocument();
    expect(
      screen.getByRole("button", { name: "Delete child record" }),
    ).toBeInTheDocument();
    fireEvent.click(screen.getByRole("button", { name: "Fill name" }));
    fireEvent.click(screen.getByRole("button", { name: "Save" }));
    await waitFor(() => {
      const call = mockAuthFetch.mock.calls.find(
        ([, tag]) => tag === "update child record",
      );
      expect(call && JSON.parse(call[2].body).record_id).toBe(7);
    });
  });

  it("keeps create-only capability on an empty form and respects the embedding restriction", async () => {
    mockCanvasCapabilities = {
      can_create: true,
      can_edit: false,
      can_delete: false,
    };
    mockAuthFetch.mockResolvedValue({ rows: [] });
    const { rerender } = render(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
      />,
    );
    expect(
      await screen.findByRole("button", { name: "Save New" }),
    ).toBeInTheDocument();
    rerender(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        allowCreate={false}
      />,
    );
    expect(
      screen.queryByRole("button", { name: "Save New" }),
    ).not.toBeInTheDocument();
  });

  it("submits a new child record through the child canvas with both host levels", async () => {
    mockAuthFetch.mockImplementation(async (_url, tag) =>
      tag === "fetch child records" ? { rows: [] } : { success: true },
    );
    render(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    fireEvent.click(await screen.findByRole("button", { name: "Fill name" }));
    fireEvent.click(screen.getByRole("button", { name: "Save New" }));
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/canvases/child-canvas/submit?parent_row_id=parent-1&embedding_path=outer-canvas%3Aouter-host&embedding_path=host-canvas%3Ahost-element",
        "create child record",
        expect.objectContaining({
          method: "POST",
          body: JSON.stringify({
            data_source_alias: "main",
            data: { name: "Changed" },
          }),
        }),
      ),
    );
  });

  it("submits edits and deletes using the child context", async () => {
    let fetchCount = 0;
    mockAuthFetch.mockImplementation(async (_url, tag) => {
      if (tag === "fetch child records") {
        fetchCount += 1;
        return {
          rows: [
            { __row_id: "row-1", name: fetchCount === 1 ? "Old" : "Refetched" },
          ],
        };
      }
      return { success: true };
    });
    render(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    fireEvent.click(await screen.findByRole("button", { name: "Fill name" }));
    fireEvent.click(screen.getByRole("button", { name: "Save" }));
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/canvases/child-canvas/submit?parent_row_id=parent-1&embedding_path=outer-canvas%3Aouter-host&embedding_path=host-canvas%3Ahost-element",
        "update child record",
        expect.objectContaining({
          method: "POST",
          body: JSON.stringify({
            data_source_alias: "main",
            record_id: "row-1",
            data: { name: "Changed" },
          }),
        }),
      ),
    );
    // The delete control can already exist before the save finishes. Wait for
    // the post-save fetch to render before clicking it.
    await screen.findByText("Refetched");
    fireEvent.click(
      screen.getByRole("button", { name: "Delete child record" }),
    );
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/view-rows/view-1/rows/row-1?canvas_id=child-canvas&embedding_path=outer-canvas%3Aouter-host&embedding_path=host-canvas%3Ahost-element",
        "delete child record",
        expect.objectContaining({ method: "DELETE" }),
      ),
    );
  });
  it("uses the query API _rowId for form saves and deletes", async () => {
    const id = "02d52481-546f-5694-9a49-d8359ec0e7c0";
    let fetchCount = 0;
    mockAuthFetch.mockImplementation(async (_url, tag) =>
      tag === "fetch child records"
        ? {
            data: [
              {
                _rowId: id,
                name: ++fetchCount === 1 ? "Original" : "Refetched after save",
              },
            ],
          }
        : { success: true },
    );
    render(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    fireEvent.click(await screen.findByRole("button", { name: "Fill name" }));
    fireEvent.click(screen.getByRole("button", { name: "Save" }));
    await waitFor(() => {
      const call = mockAuthFetch.mock.calls.find(
        ([, tag]) => tag === "update child record",
      );
      expect(call && JSON.parse(call[2].body).record_id).toBe(id);
    });
    // The delete button from the original row remains mounted during the
    // post-save refetch. Wait for the new query result before attempting the
    // next write so we don't race the component's loading guard.
    await screen.findByText("Refetched after save");
    fireEvent.click(
      screen.getByRole("button", { name: "Delete child record" }),
    );
    await waitFor(() =>
      expect(
        mockAuthFetch.mock.calls.some(
          ([url, tag]) =>
            tag === "delete child record" && url.includes(`/rows/${id}?`),
        ),
      ).toBe(true),
    );
  });

  it("refuses writes when the query row has no identity", async () => {
    mockAuthFetch.mockResolvedValue({ data: [{ name: "Identity missing" }] });
    render(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    expect(
      await screen.findByText(/missing.*record.*identifier/i),
    ).toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "Save" }),
    ).not.toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "Delete child record" }),
    ).not.toBeInTheDocument();
    expect(
      mockAuthFetch.mock.calls.every(
        ([, tag]) => tag === "fetch child records",
      ),
    ).toBe(true);
  });
  it("preserves query identities for multiple embedded records", async () => {
    const first = "02d52481-546f-5694-9a49-d8359ec0e7c0";
    const second = "3cd52481-546f-5694-9a49-d8359ec0e7c0";
    mockAuthFetch.mockImplementation(async (_url, tag) =>
      tag === "fetch child records"
        ? {
            data: [
              { _rowId: first, name: "First" },
              { _rowId: second, name: "Second" },
            ],
          }
        : { success: true },
    );
    render(
      <EmbeddedCanvasRenderer
        element={hostElement as any}
        parentData={{ id: "parent-key", __row_id: "parent-1" }}
        workspaceId="workspace-1"
        parentCanvasId="host-canvas"
      />,
    );
    const edit = await screen.findAllByTestId("EditIcon");
    fireEvent.click(edit[1]);
    fireEvent.click(screen.getByTestId("SaveIcon"));
    await waitFor(() => {
      const call = mockAuthFetch.mock.calls.find(
        ([, tag]) => tag === "update child record",
      );
      expect(call && JSON.parse(call[2].body).record_id).toBe(second);
    });
  });
});
